/**
 * Walk it through, step 2: the room as a guest sees it, offer included.
 *
 * It shows the room at any second of the talk, with the talk's timeline under
 * it: chapters as bands, every moment and the offer as a pin, a + wherever a
 * moment could go. Clicking a moment or the offer (in the room, on its pin, or
 * in the list beside) opens its fields; + writes a new moment from what is said
 * there. Clicking the track shows that second; dragging a pin moves it.
 *
 * A moment's words and the offer's are typed straight onto the room, as guests
 * read them, or in the fields named after what the card shows.
 */
import { useEffect, useMemo, useRef, useState, type MouseEvent, type PointerEvent } from "react";
import type { BoardState } from "../../../shared/types";
import { Icon } from "../../components/ui/Icon";
import { cx } from "../../components/ui/cx";
import { useToast } from "../../components/ui/Toast";
import type { BoardHost, MomentKind } from "../host";
import { chapterRowsOf } from "../make/MakeChapters";
import { mmss, secondsAt } from "../make/time";
import { openAssets } from "../../assets/store";
import { useNoteMedia } from "./noteMedia";
import { handoutPick, offerFilePick, roomEditing, videoNotePick } from "./roomEdits";
import { useCardPlace } from "./placeCard";
import { RoomPreview } from "./RoomPreview";
import {
  fileFace,
  gapsIn,
  KIND,
  minuteMark,
  type Moment,
  momentsOf,
  momentWords,
  NEW_BTN,
  type Offer,
  offerWords,
  offerOf,
  onScreen,
  PIN_END,
  PIN_START,
  roomDuration,
  useRoomFiles,
} from "./roomParts";
import { FieldsCard, MomentFields, OFFER_KINDS, OfferFields } from "./WalkFields";

interface WalkRoomProps {
  state: BoardState;
  host: BoardHost;
  phone: boolean;
  /** The open card: "mo:<card id>", "addmo:<second>" or "ofwhen" (the offer). */
  open: string | null;
  onOpen: (key: string | null) => void;
  /** Words typed in place or a pin dragged: its way back, for the bar's Undo; returns the way back to run now. */
  onTyped?: (undo: (now: BoardState) => void) => () => void;
  /** The room's name as it is being typed in the bar. */
  title?: string;
}

export function WalkRoom({ state, host, phone, open, onOpen, onTyped, title }: WalkRoomProps) {
  const toast = useToast();
  // The files on the room's cards are named and sized as Assets has them.
  const lib = useRoomFiles(state);
  const moments = useMemo(() => momentsOf(state), [state]);
  const offer = offerOf(state);
  /** The part that opened the card: its pin on the timeline, the thing in the room, or the chip beside it. */
  const [from, setFrom] = useState<"pin" | "part" | "chip">("part");
  const chapters = chapterRowsOf(state);
  const live = host.server.scheduleMode === "live";
  const dur = roomDuration(state, host);
  const first = moments.find((m) => m.on);
  const [t, setT] = useState(() => (first ? (first.t ?? 0) + 3 : 60));
  const at = t;
  const [working, setWorking] = useState<string | null>(null);
  // Why keyed: a failed change is that card's, said once; the next card opened starts clean.
  const [failure, setFailure] = useState<{ key: string | null; text: string } | null>(null);
  const error = failure && failure.key === open ? failure.text : "";
  const setError = (text: string) => setFailure(text ? { key: open, text } : null);
  useEffect(() => setFailure(null), [open]);
  const track = useRef<HTMLDivElement>(null);
  const notePoster = useNoteMedia(offer?.videoNote).poster;
  const hostName = host.server.hostName || "Your host";
  const hostFirst = hostName.split(" ")[0];
  const ci = chapters.length ? Math.max(0, chapters.findLastIndex((c) => c.t <= at)) : -1;
  const { up, showOffer } = onScreen(state, at, live);
  const offerFile = offer?.offerType === "download" ? fileFace(lib.assets, offer.file) : null;
  /** Where the click that opened the card landed inside its part, and whether the card takes the caret. */
  const [opened, setOpened] = useState<{ at: { dx: number; dy: number } | null; focus: boolean }>({ at: null, focus: true });
  /** The control that opened a card closes it on a second press. */
  const toggle = (key: string, by: "pin" | "part" | "chip" = "part") => {
    setFrom(by);
    setOpened({ at: null, focus: true });
    onOpen(open === key ? null : key);
  };
  /**
   * A part in the room clicked: its card opens beside it. A click into its words is for typing, so it never
   * closes the card, and the card leaves the caret where it is.
   */
  const openPart = (e: MouseEvent<HTMLElement>, key: string) => {
    const inText = !!(e.target as HTMLElement).closest('[contenteditable="plaintext-only"]');
    if (open === key && from === "part") {
      if (!inText) onOpen(null);
      return;
    }
    const r = e.currentTarget.getBoundingClientRect();
    setFrom("part");
    setOpened({ at: { dx: e.clientX - r.left, dy: e.clientY - r.top }, focus: !inText });
    onOpen(key);
  };
  /** A part's own classes: open (`bw-sel`). */
  const partMarks = (key: string, by: "pin" | "part" | "chip") => cx(open === key && from === by && "bw-sel");
  const { typeOn, typedOn } = roomEditing(host, onTyped);
  const pct = (s: number) => `${Math.max(0, Math.min(100, (s / dur) * 100)).toFixed(3)}%`;

  const when = (sec: number) => (live ? `about ${Math.round(sec / 60)} minutes in` : `at ${mmss(sec)}`);
  /** A room with no offer gets one: five minutes before the end, on a whole minute, and its card opens for the words. */
  const placeOffer = (k: Offer["offerType"]) => {
    const sec = Math.max(120, Math.floor((dur - 300) / 60) * 60);
    const card = { id: "bc_offer", kind: "offer", label: "The offer", on: true, offerType: k, btn: NEW_BTN[k], url: "", code: "", t: sec } as Offer;
    host.dispatch({ type: "add", lane: "room", card });
    setFrom("pin");
    setOpened({ at: null, focus: true });
    onOpen("ofwhen");
    host.toast(`${OFFER_KINDS.find((x) => x.type === k)?.label ?? "The offer"} shows ${when(sec)}.`);
  };
  // Why it waits: a recording's end is only known once its length comes in, and a press before that put the offer at 2:00.
  const [offerAsked, setOfferAsked] = useState<Offer["offerType"] | null>(null);
  const addOffer = (k: Offer["offerType"]) => (live || host.lengthKnown ? placeOffer(k) : setOfferAsked(k));
  useEffect(() => {
    if (!offerAsked || !host.lengthKnown) return;
    setOfferAsked(null);
    placeOffer(offerAsked);
    // `placeOffer` is drawn anew each time; the press and the length are what matter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerAsked, host.lengthKnown]);

  // Why: the offer's card opens on the room showing it, as a guest sees it at that second.
  useEffect(() => {
    if (open === "ofwhen" && offer) setT(Math.min(dur - 1, (offer.t ?? 0) + 4));
    // The card opening and the offer arriving are what matter; a dragged or retimed offer keeps the second it was moved to.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, offer?.id]);

  const room = (
    <RoomPreview
      state={state}
      host={host}
      at={at}
      phone={phone}
      title={title}
      edits={{ typeOn, typedOn, marks: (key) => partMarks(key, "part"), openPart }}
    />
  );
  const stage = phone ? (
    <div className="bw-phwrap">
      <div className="bw-phone bw-auto">
        <div className="bw-phone-in">{room}</div>
      </div>
    </div>
  ) : (
    room
  );

  // ── the timeline under the room ───────────────────────────────────────────
  const marks = moments
    .map((m) => ({ key: `mo:${m.id}`, id: m.id, kind: m.kind as Moment["kind"] | "offer", t: m.t ?? 0, on: m.on, label: KIND[m.kind].name, words: momentWords(m) }))
    .concat(offer ? [{ key: "ofwhen", id: offer.id, kind: "offer" as const, t: offer.t ?? 0, on: true, label: "The offer", words: offerWords(offer, offerFile?.name) }] : [])
    .toSorted((a, b) => a.t - b.t);
  const near = marks.some((m) => Math.abs(m.t - at) < 40) || at > dur - 30;
  const plus = gapsIn(marks.map((m) => m.t), dur).filter((g) => near || Math.abs(g - at) > 100);
  /** The second under a point on the track, on a five-second step. */
  const secondAt = (x: number): number | null => {
    const r = track.current?.getBoundingClientRect();
    if (!r || r.width === 0) return null;
    return Math.round((Math.max(0, Math.min(1, (x - r.left) / r.width)) * dur) / 5) * 5;
  };
  const seekTo = (x: number) => {
    const s = secondAt(x);
    if (s !== null) setT(Math.min(dur - 1, s));
  };
  /** A pin being dragged: where it is now, shown on the pin and its tip until it is let go. */
  const [dragged, setDragged] = useState<{ key: string; t: number } | null>(null);
  const press = useRef<{ key: string | null; x0: number; moved: boolean; t: number | null } | null>(null);
  /** A drag ends in a click on the pin; that click is not a press to open its card. */
  const dropped = useRef(false);
  /** A pin let go at a new second: the moment (or the offer) moves there, with the way back. */
  const movePin = (key: string, to: number) => {
    const card = key === "ofwhen" ? offerOf(state) : momentsOf(state).find((m) => `mo:${m.id}` === key);
    if (!card || (card.t ?? 0) === to) return;
    const was = card.t ?? 0;
    typeOn(card.id, { t: to });
    setT(Math.min(dur - 1, to + (card.kind === "offer" ? 5 : 2)));
    const name = card.kind === "offer" ? "The offer" : KIND[card.kind as Moment["kind"]].name;
    const undo = onTyped?.(() => typeOn(card.id, { t: was }));
    toast({ text: `${name} moved to ${mmss(to)}.`, icon: "check", action: { label: "Undo", onClick: () => (undo ? undo() : typeOn(card.id, { t: was })) } });
  };
  // Why on the window: a drag goes on wherever the pointer goes, off the track and back.
  useEffect(() => {
    const moveTo = (e: globalThis.PointerEvent) => {
      const p = press.current;
      if (!p) return;
      if (Math.abs(e.clientX - p.x0) > 4) p.moved = true;
      if (!p.key) {
        seekTo(e.clientX);
        return;
      }
      const s = secondAt(e.clientX);
      if (!p.moved || s === null) return;
      p.t = Math.max(PIN_START, Math.min(s, dur - PIN_END));
      setDragged({ key: p.key, t: p.t });
    };
    const letGo = () => {
      const p = press.current;
      press.current = null;
      if (!p?.key) return;
      setDragged(null);
      if (!p.moved || p.t === null) return;
      dropped.current = true;
      setTimeout(() => (dropped.current = false), 0);
      movePin(p.key, p.t);
    };
    const cancel = () => {
      press.current = null;
      setDragged(null);
    };
    window.addEventListener("pointermove", moveTo);
    window.addEventListener("pointerup", letGo);
    window.addEventListener("pointercancel", cancel);
    return () => {
      window.removeEventListener("pointermove", moveTo);
      window.removeEventListener("pointerup", letGo);
      window.removeEventListener("pointercancel", cancel);
    };
  });
  /** The track or a pin pressed: the track shows that second (and follows the pointer); a pin waits to see if it is dragged. */
  const pressDown = (e: PointerEvent<HTMLDivElement>) => {
    const el = e.target as HTMLElement;
    if (e.button !== 0 || el.closest(".bw-plus")) return;
    const pin = el.closest<HTMLElement>(".bw-pin");
    if (!pin && !el.closest(".bw-tl-track, .bw-tl-pins")) return;
    e.preventDefault();
    press.current = { key: pin?.dataset.bw ?? null, x0: e.clientX, moved: false, t: null };
    if (!pin) seekTo(e.clientX);
  };
  const tickStep = dur > 3000 ? 600 : 300;
  const ticks: number[] = [];
  for (let s = 0; s < dur - tickStep * 0.55; s += tickStep) ticks.push(s);
  const timeline = (
    <div className="bw-tl" onPointerDown={pressDown}>
      <div className="bw-tl-h">
        <b>{live ? "The plan" : "The talk"}</b>
        <span>
          {live ? `${Math.round(dur / 60)} minutes, live` : `${mmss(dur)} · ${chapters.length} chapters`} · {moments.length} {moments.length === 1 ? "moment" : "moments"}
          {offer ? " and the offer" : ""}
        </span>
      </div>
      <div className="bw-tl-pins">
        {plus.map((g) => (
          <button type="button" key={`p${g}`} className={cx("bw-plus", partMarks(`addmo:${g}`, "pin"))} data-bw={`addmo:${g}`} style={{ left: pct(g) }} title={`Add a moment around ${mmss(g)}`} aria-label={`Add a moment around ${mmss(g)}`} onClick={() => toggle(`addmo:${g}`, "pin")}>
            <Icon name="plus" />
          </button>
        ))}
        {near ? null : (
          <button type="button" className={cx("bw-plus bw-hplus", partMarks(`addmo:${at}`, "pin"))} data-bw={`addmo:${at}`} style={{ left: pct(at) }} title={`Add a moment at ${mmss(at)}`} aria-label={`Add a moment at ${mmss(at)}`} onClick={() => toggle(`addmo:${at}`, "pin")}>
            <Icon name="plus" />
          </button>
        )}
        {marks.map((x) => {
          const moving = dragged?.key === x.key ? dragged.t : null;
          const tm = moving ?? x.t;
          return (
            <button
              type="button"
              key={x.key}
              className={cx("bw-pin", `k-${x.kind}`, !x.on && "off", moving !== null && "drag", partMarks(x.key, "pin"))}
              data-bw={x.key}
              style={{ left: pct(tm) }}
              aria-label={`${x.label} at ${mmss(x.t)}`}
              onClick={() => {
                if (dropped.current) return;
                setT(x.t + 3);
                toggle(x.key, "pin");
              }}
            >
              <span className="bw-tip">
                <b>
                  {x.label} · {mmss(tm)}
                  {x.on ? "" : " · off"}
                </b>
                <span>{x.words}</span>
              </span>
              <Icon name={x.kind === "offer" ? "gift" : KIND[x.kind].icon} />
            </button>
          );
        })}
      </div>
      <div className="bw-tl-track" ref={track} role="slider" tabIndex={0} aria-label="The second shown" aria-valuemin={0} aria-valuemax={dur} aria-valuenow={at} aria-valuetext={mmss(at)}
        onKeyDown={(e) => {
          if (e.key === "ArrowRight") setT((x) => Math.min(dur - 1, x + 5));
          if (e.key === "ArrowLeft") setT((x) => Math.max(0, x - 5));
        }}
      >
        <div className="bw-tl-bands">
          {live ? (
            <span className="bw-liveband" style={{ left: 0, width: "100%" }}>
              <Icon name="live" /> Live for {Math.round(dur / 60)} minutes
            </span>
          ) : (
            chapters.map((c, i) => {
              const end = i < chapters.length - 1 ? chapters[i + 1].t : dur;
              return (
                <span key={`${c.t}-${i}`} className={cx(i % 2 === 1 && "alt", i === ci && "cur")} style={{ left: pct(c.t), width: `${(((end - c.t) / dur) * 100).toFixed(3)}%` }} title={`${i + 1}. ${c.title}`}>
                  <b>{i + 1}</b> {c.title}
                </span>
              );
            })
          )}
        </div>
        <i className="bw-head" style={{ left: pct(at) }}>
          <span>{mmss(at)}</span>
        </i>
      </div>
      <div className="bw-tl-ruler">
        {ticks.map((s) => (
          <span key={s} style={{ left: pct(s) }}>
            {Math.round(s / 60)}:00
          </span>
        ))}
        <span className="end">{mmss(dur)}</span>
      </div>
    </div>
  );

  // ── the card that is open ─────────────────────────────────────────────────
  const cardFor = (key: string) => {
    if (key.startsWith("mo:")) {
      const m = momentsOf(state).find((x) => x.id === key.slice(3));
      if (!m) return null;
      const tm = m.t ?? 0;
      const name = KIND[m.kind].name.toLowerCase();
      return (
        <FieldsCard
          key={key}
          title={`The ${name} ${when(tm)}`}
          onClose={() => onOpen(null)}
          foot={
            <button
              type="button"
              className="btn s"
              onClick={() => {
                host.dispatch({ type: "delete", id: m.id });
                onOpen(null);
                toast({ text: `The ${name} is out.`, icon: "check", action: { label: "Undo", onClick: () => host.dispatch({ type: "restore", id: m.id }) } });
              }}
            >
              Take it out
            </button>
          }
        >
          <MomentFields
            moment={m}
            live={live}
            off={false}
            edit={typeOn}
            typed={typedOn}
            onTime={(sec) => movePin(key, Math.max(PIN_START, Math.min(sec, dur - PIN_END)))}
            file={handoutPick(m, lib.assets, { typeOn, typedOn })}
          />
        </FieldsCard>
      );
    }
    if (key === "ofwhen") {
      if (!offer) return null;
      return (
        <FieldsCard key={key} title={`The offer ${when(offer.t ?? 0)}`} onClose={() => onOpen(null)}>
          <OfferFields
            offer={offer}
            live={live}
            off={false}
            edit={typeOn}
            typed={typedOn}
            onTime={(sec) => movePin("ofwhen", Math.max(PIN_START, Math.min(sec, dur - PIN_END)))}
            file={offerFilePick(offer, lib.assets, { typeOn, typedOn })}
            videoNote={videoNotePick(offer, lib.assets, notePoster, mmss, { typeOn, typedOn })}
          />
          {host.server.publishBlocks.length ? (
            <div className="bw-need">
              <Icon name="info" />
              <span>{host.server.publishBlocks[0]}</span>
            </div>
          ) : null}
        </FieldsCard>
      );
    }
    if (key.startsWith("addmo:")) {
      const tm = Number(key.slice(6)) || 0;
      const add = async (kind: MomentKind) => {
        setWorking("addmo");
        const made = await host.addMomentAt(kind, minuteMark(tm));
        setWorking(null);
        if (made) onOpen(`mo:${made.id}`);
        else setError(host.opError || "That moment could not be written. Try again.");
      };
      const handout = () =>
        openAssets({
          fits: "handout",
          title: `A handout at ${mmss(tm)}`,
          cta: "Hand this out",
          onPick: (a) => {
            if (!a.key) return;
            const id = `bc_handout_${Date.now().toString(36)}`;
            host.dispatch({ type: "add", lane: "room", card: { id, kind: "handout", label: "Handout", on: true, t: tm, name: a.name, key: a.key } });
            onOpen(`mo:${id}`);
          },
        });
      const moveTo = (typed: string) => {
        const sec = secondsAt(typed.includes(":") ? typed : `${typed}:00`);
        if (sec === null || sec > dur - 30) {
          toast({ text: `Type a time between 00:00 and ${mmss(dur - 30)}.`, icon: "info" });
          return false;
        }
        if (sec !== tm) onOpen(`addmo:${sec}`);
        return true;
      };
      return <NewMoment key={key} at={tm} live={live} busy={working === "addmo"} error={error} onAt={moveTo} onPick={(k) => void add(k)} onHandout={handout} onClose={() => onOpen(null)} />;
    }
    return null;
  };

  // ── beside the room: what is on screen, and every moment in order ─────────
  const order = (
    <aside className="bw-order">
      <div className="bw-now">
        <small>On screen {live ? `${Math.floor(at / 60)} minutes in` : `at ${mmss(at)}`}</small>
        <b>{live ? `${hostFirst}, live` : ci >= 0 ? `Chapter ${ci + 1} · ${chapters[ci].title}` : "The talk"}</b>
        <span>{up ? `The ${KIND[up.kind].name.toLowerCase()} is up.` : showOffer ? "The offer is under the talk." : "Just the talk."}</span>
      </div>
      {offer ? null : (
        <div className="bw-fld bw-addoffer">
          <span>Add an offer</span>
          <div className="row wrap">
            {OFFER_KINDS.map((k) => (
              <button type="button" key={k.type} className="btn s" onClick={() => addOffer(k.type)}>
                {k.label}
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="bw-order-h">Moments</div>
      {marks.map((r) => (
        <button
          type="button"
          key={r.key}
          className={cx("bw-orow", !r.on && "off", ((up && up.id === r.id) || (r.kind === "offer" && showOffer && !up)) && "cur")}
          onClick={() => {
            setT(r.t + 3);
            // Why from the pin: the prototype opens a moment's card on its pin when its row is clicked.
            toggle(r.key, "pin");
          }}
        >
          <span className={cx("nbk", `k-${r.kind}`)}>
            <Icon name={r.kind === "offer" ? "gift" : KIND[r.kind].icon} />
          </span>
          <span className="tx">
            <span className="l">
              {r.label}
              <em>{live ? `${Math.round(r.t / 60)} min` : mmss(r.t)}</em>
              {r.on ? null : <i>Off</i>}
            </span>
            <span className="w">{r.words}</span>
          </span>
        </button>
      ))}
    </aside>
  );

  // The open card sits by the part that opened it: its pin, the thing in the room, or the chip beside it.
  const stepEl = useRef<HTMLDivElement>(null);
  const cards = useCardPlace({
    key: open,
    pinned: from === "pin",
    at: opened.at,
    part: () => {
      if (!open) return null;
      const q = CSS.escape(open);
      const sel = from === "pin" ? `.bw-tl [data-bw="${q}"]` : `.bw-room [data-bw="${q}"]`;
      return stepEl.current?.querySelector(sel)?.getBoundingClientRect() ?? null;
    },
    deps: [state, at, phone, dragged],
  });

  return (
    <div className="bw-roomstep" ref={stepEl}>
      <div className="bw-roomgrid">
        <div className="bw-roomcol">{stage}</div>
        {order}
      </div>
      {timeline}
      {open ? (
        <div className="bw-cards" ref={cards}>
          {cardFor(open)}
        </div>
      ) : null}
    </div>
  );
}

/** "A new moment at 12:40": what kind, written from what is said there. A handout takes a file. */
function NewMoment({
  at,
  live,
  busy,
  error,
  onAt,
  onPick,
  onHandout,
  onClose,
}: {
  at: number;
  live: boolean;
  busy: boolean;
  error: string;
  /** The second typed in At; false when it is not one the moment can go at. */
  onAt: (typed: string) => boolean;
  onPick: (k: MomentKind) => void;
  onHandout: () => void;
  onClose: () => void;
}) {
  const when = live ? `about ${Math.round(at / 60)} minutes in` : `at ${mmss(at)}`;
  const [typed, setTyped] = useState(mmss(at));
  const take = () => {
    if (!onAt(typed.trim())) setTyped(mmss(at));
  };
  return (
    <div className={cx("bw-card bw-chat in", busy && "busy")} role="dialog" aria-label={`A new moment ${when}`}>
      <div className="bw-ch">
        <span className="bw-cl">A new moment {when}</span>
        <button type="button" className="ibtn sm" aria-label="Close" onClick={onClose}>
          <Icon name="x" />
        </button>
      </div>
      <div className="bw-crow">
        <label htmlFor="bw-at">{live ? "Minute" : "At"}</label>
        <input dir="auto"
          className="inp nbtime"
          id="bw-at"
          value={typed}
          inputMode="numeric"
          spellCheck={false}
          autoComplete="off"
          onChange={(e) => setTyped(e.target.value)}
          onBlur={take}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              take();
            }
          }}
        />
        <span>{live ? "into the session" : "into the talk"}</span>
      </div>
      <div className="bw-cb">
        <div className="bw-m fl">
          <span className="bw-av" aria-hidden="true">
            <Icon name="sparkle" />
          </span>
          <div className={cx("bw-mb", busy && "bw-dots")}>{busy ? <><i /><i /><i /></> : <p>{live ? `What should happen ${when}?` : `What should happen here? I write it from what is said at ${mmss(at)}.`}</p>}</div>
        </div>
      </div>
      {busy ? null : (
        <div className="bw-picks">
          <button type="button" className="bw-pick kind" onClick={() => onPick("poll")}>
            <Icon name="bars" />A poll
          </button>
          <button type="button" className="bw-pick kind" onClick={() => onPick("question")}>
            <Icon name="question" />A question
          </button>
          <button type="button" className="bw-pick kind" onClick={() => onPick("reaction")}>
            <Icon name="heart" />A reaction
          </button>
          <button type="button" className="bw-pick kind" onClick={onHandout}>
            <Icon name="file" />A handout
          </button>
        </div>
      )}
      {error ? (
        <p className="bw-cerr" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
